const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export const formatSegmentDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${MONTHS[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`;
};

export const formatRelativeAsOf = (iso: string, nowMs = Date.now()): string => {
  const ms = nowMs - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return `on ${formatSegmentDate(iso)}`;
};

export const formatMembersLabel = (count: number): string =>
  `~${count.toLocaleString()} member${count === 1 ? '' : 's'}`;

export const formatSegmentsSubtitle = (options: {
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  searchQuery?: string;
}): string => {
  const { isLoading, isError, totalCount, searchQuery } = options;

  if (isLoading) return 'Loading…';
  if (isError) return 'Could not load segments.';

  const countLabel = `${totalCount} segment${totalCount === 1 ? '' : 's'}`;
  if (searchQuery) {
    return `${countLabel} matching "${searchQuery}"`;
  }

  return countLabel;
};

export const getSegmentUpdatedDate = (segment: {
  lastUpdatedDate?: string | null;
  createdDate?: string | null;
}): string | null | undefined => segment.lastUpdatedDate ?? segment.createdDate;

/** Build a unique duplicate name from visible segment names (prototype: `Name (copy)`, `Name (copy) 2`, …). */
export const buildDuplicateSegmentName = (
  sourceName: string | null | undefined,
  existingNames: string[]
): string => {
  const trimmed = sourceName?.trim() || 'Untitled segment';
  const base = `${trimmed} (copy)`;
  const normalizedExisting = new Set(existingNames.map((name) => name.trim().toLowerCase()));

  if (!normalizedExisting.has(base.toLowerCase())) {
    return base;
  }

  let counter = 2;
  while (counter < 30) {
    const candidate = `${base} ${counter}`;
    if (!normalizedExisting.has(candidate.toLowerCase())) {
      return candidate;
    }
    counter += 1;
  }

  return `${base} ${counter}`;
};
